import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, MapPin, CurrencyDollar, CalendarBlank, FileText, Check, DownloadSimple, ImageSquare, Buildings, PaperPlaneTilt, CloudArrowUp, X, Phone, EnvelopeSimple, User, Ruler, FilePdf, Blueprint, ArrowDown, Eye, Clock, Gavel, UsersThree, Camera, ShieldCheck, FloppyDisk } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ───
const classifyDoc = (filename) => {
  const lower = (filename || '').toLowerCase();
  if (lower.match(/draw|plan|floor|elev|detail|layout|blueprint/)) return 'drawings';
  if (lower.match(/addend|addendum|amend|change.?order|bulletin/)) return 'addenda';
  return 'specs';
};

const DOC_CATEGORIES = [
  { key: 'drawings', label: 'Drawings', icon: Blueprint },
  { key: 'specs', label: 'Specs', icon: Ruler },
  { key: 'addenda', label: 'Addenda', icon: FileText },
];

const openDocument = async (docId, filename) => {
  try {
    const res = await axios.get(`${API}/files/${docId}/download`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch { toast.error(`Failed to open ${filename}`); }
};

const downloadDoc = async (docId, filename) => {
  try {
    const res = await axios.get(`${API}/files/${docId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch { toast.error(`Failed to download ${filename}`); }
};

// ─── Section number badge ───
const SectionNum = ({ num }) => (
  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold" style={{ background: 'rgba(201,206,211,0.1)', color: 'var(--gold)', border: '1px solid rgba(201,206,211,0.15)' }}>
    {num}
  </div>
);

const UploadZone = ({ label, hint, testId, onUpload }) => {
  const onDrop = async (acceptedFiles) => { await onUpload(acceptedFiles); };
  const dz = useDropzone({ onDrop, accept: { 'application/pdf': ['.pdf'], 'image/*': ['.jpg', '.jpeg', '.png'], 'application/msword': ['.doc'], 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] }, multiple: true, maxSize: 10 * 1024 * 1024 });
  return (
    <div>
      <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>{label} <span style={{ color: 'var(--text-muted)' }}>{hint}</span></Label>
      <div {...dz.getRootProps()} data-testid={testId} className={`hb-upload-zone rounded-lg p-4 text-center cursor-pointer ${dz.isDragActive ? 'active' : ''}`}>
        <input {...dz.getInputProps()} />
        <CloudArrowUp size={22} className="mx-auto mb-1" style={{ color: 'var(--text-muted)' }} />
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{dz.isDragActive ? 'Drop here...' : 'Drag & drop or click'}</p>
        <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>PDF, DOC, JPG, PNG — 10MB max</p>
      </div>
    </div>
  );
};

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, adminView } = useContext(AuthContext);
  const [project, setProject] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoUrl, setPhotoUrl] = useState(null);

  // Bid form state
  const [bidData, setBidData] = useState({ amount: '', proposal: '', estimated_duration: '', contact_name: '', company_name: '', email: '', phone: '' });
  const [submittingBid, setSubmittingBid] = useState(false);
  const [bidSubmitted, setBidSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [existingBid, setExistingBid] = useState(null);
  const [showBidModal, setShowBidModal] = useState(false);
  const bidFormRef = useRef(null);
  // Subcontractor state
  const [subcontractors, setSubcontractors] = useState([]);
  const [newSub, setNewSub] = useState('');
  // Resubmit edit mode
  const [editMode, setEditMode] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  useEffect(() => { fetchProject(); }, [projectId]);
  useEffect(() => { if (user.role === 'owner' || (user.role === 'admin' && adminView !== 'gc')) fetchBids(); }, [projectId]);
  useEffect(() => { if (user.role === 'gc' || (user.role === 'admin' && adminView === 'gc')) fetchMyBid(); }, [projectId]);
  useEffect(() => {
    if (user && (user.role === 'gc' || user.role === 'admin')) {
      setBidData(prev => ({
        ...prev,
        contact_name: prev.contact_name || user.name || '',
        company_name: prev.company_name || user.company || '',
        email: prev.email || user.email || '',
        phone: prev.phone || user.phone || '',
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!project?.photos?.length) return;
    const raw = project.photos[0];
    const fileId = raw.includes('/') ? raw.split('/').pop().split('.')[0] : raw;
    let blobUrl = null;
    axios.get(`${API}/files/${fileId}/download`, { responseType: 'blob' })
      .then(res => { blobUrl = window.URL.createObjectURL(res.data); setPhotoUrl(blobUrl); })
      .catch(() => setPhotoUrl(null));
    return () => { if (blobUrl) window.URL.revokeObjectURL(blobUrl); };
  }, [project]);

  const fetchProject = async () => {
    try { const r = await axios.get(`${API}/projects/${projectId}`); setProject(r.data); }
    catch { toast.error('Failed to load project'); }
    finally { setLoading(false); }
  };

  const fetchBids = async () => {
    try { const r = await axios.get(`${API}/projects/${projectId}/bids`); setBids(r.data); }
    catch { /* ok */ }
  };

  const fetchMyBid = async () => {
    try {
      const r = await axios.get(`${API}/bids/my-bids`);
      const mine = r.data.find(b => b.project_id === projectId);
      if (mine) {
        setExistingBid(mine);
        // Pre-populate form if it's a draft so the GC can keep working
        if (mine.status === 'draft') {
          setBidData(prev => ({
            ...prev,
            amount: mine.amount?.toString() || prev.amount,
            proposal: mine.proposal || prev.proposal,
            estimated_duration: mine.estimated_duration || prev.estimated_duration,
          }));
        }
      }
    } catch { /* ok */ }
  };

  const scrollToBidForm = () => {
    bidFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    setSubmittingBid(true);
    try {
      const attachmentPaths = uploadedFiles.map(f => f.path);
      await axios.post(`${API}/bids`, {
        project_id: projectId,
        amount: parseFloat(bidData.amount),
        proposal: bidData.proposal + (subcontractors.length > 0 ? `\n\n--- Subcontractors ---\n${subcontractors.join('\n')}` : ''),
        estimated_duration: bidData.estimated_duration,
        attachment_paths: attachmentPaths,
      });
      toast.success('Bid submitted successfully!');
      setBidSubmitted(true);
      setShowBidModal(false);
      fetchProject();
      fetchMyBid();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit bid'); }
    finally { setSubmittingBid(false); }
  };

  const handleResubmitBid = async (e) => {
    e.preventDefault();
    setResubmitting(true);
    try {
      const attachmentPaths = uploadedFiles.map(f => f.path);
      await axios.post(`${API}/bids/${existingBid.id}/resubmit`, {
        amount: parseFloat(bidData.amount),
        proposal: bidData.proposal + (subcontractors.length > 0 ? `\n\n--- Subcontractors ---\n${subcontractors.join('\n')}` : ''),
        estimated_duration: bidData.estimated_duration,
        attachment_paths: attachmentPaths,
      });
      toast.success('Bid resubmitted! The owner has been notified.');
      setEditMode(false);
      fetchMyBid();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resubmit bid'); }
    finally { setResubmitting(false); }
  };

  const enterEditMode = () => {
    setBidData({
      amount: existingBid.amount?.toString() || '',
      proposal: existingBid.proposal || '',
      estimated_duration: existingBid.estimated_duration || '',
      contact_name: user.name || '',
      company_name: user.company || '',
      email: user.email || '',
      phone: user.phone || '',
    });
    setUploadedFiles([]);
    setEditMode(true);
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const attachmentPaths = uploadedFiles.map(f => f.path);
      await axios.post(`${API}/bids/save-draft`, {
        project_id: projectId,
        amount: parseFloat(bidData.amount) || 0,
        proposal: bidData.proposal,
        estimated_duration: bidData.estimated_duration,
        attachment_paths: attachmentPaths,
      });
      toast.success('Draft saved! You can come back anytime to finish.');
      setDraftSaved(true);
      fetchMyBid();
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save draft'); }
    finally { setSavingDraft(false); }
  };

  const handleFileUpload = async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      const fileId = `${file.name}-${Date.now()}`;
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
      try {
        const fd = new FormData(); fd.append('file', file);
        const res = await axios.post(`${API}/bids/upload-attachment`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (p) => { setUploadProgress(prev => ({ ...prev, [fileId]: Math.round((p.loaded * 100) / p.total) })); },
        });
        setUploadedFiles(prev => [...prev, { id: fileId, name: file.name, path: res.data.path, size: res.data.size }]);
        setTimeout(() => setUploadProgress(prev => { const n = { ...prev }; delete n[fileId]; return n; }), 1000);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
        setUploadProgress(prev => { const n = { ...prev }; delete n[fileId]; return n; });
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    accept: { 'application/pdf': ['.pdf'], 'image/*': ['.jpg', '.jpeg', '.png'], 'application/msword': ['.doc'], 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
    multiple: true, maxSize: 10 * 1024 * 1024,
  });

  const downloadBidAttachment = async (bidId, filePath, filename) => {
    try {
      const r = await axios.get(`${API}/bids/${bidId}/download-attachment?file_path=${encodeURIComponent(filePath)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error('Failed to download'); }
  };

  const handleAcceptBid = async (bidId) => {
    try {
      await axios.post(`${API}/bids/${bidId}/accept`);
      toast.success('Bid accepted! Redirecting to project hub...');
      setTimeout(() => navigate(`/projects/${projectId}/hub`), 1500);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to accept bid'); }
  };

  const getStatusColor = (s) => `hb-badge hb-badge-${s}`;

  if (loading || !project) {
    return <div className="hb-bg min-h-screen flex items-center justify-center"><img src="/harperbids-logo.png" alt="Loading" className="h-16 opacity-50 animate-pulse hb-logo-glow" /></div>;
  }

  const isGC = user.role === 'gc' || (user.role === 'admin' && adminView === 'gc');
  const canBid = isGC && !['awarded', 'completed', 'cancelled'].includes(project.status);

  // ═══════════════════════════════════════════
  //  GC VIEW — 2-column: LEFT project details, RIGHT bid workspace
  // ═══════════════════════════════════════════
  if (isGC) {
    const docs = project.documents || [];
    const grouped = { drawings: [], specs: [], addenda: [] };
    docs.forEach(d => { const cat = classifyDoc(d.filename); grouped[cat].push(d); });
    const hasAnyDocs = docs.length > 0;

    const addSubcontractor = () => { if (newSub.trim()) { setSubcontractors(prev => [...prev, newSub.trim()]); setNewSub(''); } };

    return (
      <div className="hb-bg">
        <div style={{ width: '100%' }} className="min-h-screen">
          {/* Header */}
          <header className="hb-header sticky top-0 z-50">
            <div className="max-w-screen-2xl mx-auto px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button data-testid="back-button" onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="gap-2 btn-ghost"><ArrowLeft size={20} /> Back</Button>
                <h1 className="text-lg font-bold font-heading" style={{ color: 'var(--gold-light)' }}>View & Bid</h1>
              </div>
              <div className="flex items-center gap-3">
                {['awarded', 'in_progress'].includes(project.status) && (
                  <Button data-testid="open-project-hub-button" onClick={() => navigate(`/projects/${projectId}/hub`)} className="text-sm" style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.2)', color: 'var(--gold)' }}>Project Hub</Button>
                )}
                {existingBid && existingBid.status === 'draft' && (
                  <span className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg" style={{ border: '1px solid rgba(99,102,241,0.4)', color: '#a5b4fc', background: 'rgba(99,102,241,0.08)' }}>
                    <FloppyDisk size={16} weight="bold" /> Draft Saved
                  </span>
                )}
                {((existingBid && existingBid.status !== 'draft') || bidSubmitted) && (
                  <span className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg" style={{ border: '1px solid rgba(34,197,94,0.4)', color: '#4ade80', background: 'rgba(34,197,94,0.08)' }}>
                    <Check size={16} weight="bold" /> Bid Submitted
                  </span>
                )}
              </div>
            </div>
          </header>

          {/* 2-column body */}
          <div className="max-w-screen-2xl mx-auto px-6 pt-6 pb-20 grid grid-cols-1 lg:grid-cols-5 gap-8">

            {/* ═══ LEFT COLUMN — Project Details (3/5) ═══ */}
            <div className="lg:col-span-3 space-y-8" data-testid="gc-project-left">
              {/* Project Overview */}
              <div className="hb-card overflow-hidden" data-testid="gc-project-overview">
                {photoUrl && <img src={photoUrl} alt={project.title} className="w-full h-52 object-cover" data-testid="project-hero-image" />}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <h2 className="text-2xl font-bold font-heading leading-snug" style={{ color: 'var(--gold-light)' }} data-testid="gc-project-title">{project.title}</h2>
                    <span className={`${getStatusColor(project.status)} shrink-0`}>{project.status.replace('_', ' ')}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                    <div className="flex items-center gap-2.5"><MapPin size={16} weight="duotone" style={{ color: 'var(--gold)' }} /><div><div className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Location</div><div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{project.location}</div></div></div>
                    <div className="flex items-center gap-2.5"><CurrencyDollar size={16} weight="duotone" style={{ color: 'var(--gold)' }} /><div><div className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Budget</div><div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{project.budget_range}</div></div></div>
                    <div className="flex items-center gap-2.5"><CalendarBlank size={16} weight="duotone" style={{ color: 'var(--gold)' }} /><div><div className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Bid Deadline</div><div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{new Date(project.deadline).toLocaleDateString()}</div></div></div>
                  </div>
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Description</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }} data-testid="gc-project-description">{project.description}</p>
                  </div>
                  {project.specs && (
                    <div className="pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      <h3 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Specifications / Owner Instructions</h3>
                      <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>{project.specs}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Documents */}
              <div className="hb-card p-6" data-testid="gc-documents-section">
                <h3 className="text-base font-bold font-heading mb-4 flex items-center gap-2" style={{ color: 'var(--gold-light)' }}><FileText size={18} weight="duotone" style={{ color: 'var(--gold)' }} /> Project Documents</h3>
                {hasAnyDocs ? (
                  <div className="space-y-5">
                    {DOC_CATEGORIES.map(cat => {
                      const files = grouped[cat.key];
                      if (files.length === 0) return null;
                      const Icon = cat.icon;
                      return (
                        <div key={cat.key} data-testid={`doc-category-${cat.key}`}>
                          <div className="flex items-center gap-2 mb-2"><Icon size={14} weight="duotone" style={{ color: 'var(--gold)' }} /><h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{cat.label} ({files.length})</h4></div>
                          <div className="space-y-1.5">
                            {files.map(doc => (
                              <div key={doc.id} data-testid={`gc-doc-${doc.id}`} className="flex items-center justify-between px-3 py-2.5 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                                <div className="flex items-center gap-2.5 min-w-0"><FilePdf size={18} weight="duotone" style={{ color: 'var(--gold)' }} /><div className="min-w-0"><p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{doc.filename}</p><p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{(doc.size / 1024).toFixed(0)} KB</p></div></div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <button data-testid={`open-doc-${doc.id}`} onClick={() => openDocument(doc.id, doc.filename)} className="px-2.5 py-1 rounded text-xs font-semibold" style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.15)', color: 'var(--gold)' }}>Open</button>
                                  <button data-testid={`download-doc-${doc.id}`} onClick={() => downloadDoc(doc.id, doc.filename)} className="p-1 rounded" style={{ color: 'var(--text-muted)' }}><DownloadSimple size={14} weight="bold" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6"><FileText size={36} weight="duotone" className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} /><p className="text-sm" style={{ color: 'var(--text-muted)' }}>No project documents uploaded yet.</p></div>
                )}
              </div>
            </div>

            {/* ═══ RIGHT COLUMN — Bid Workspace (2/5) ═══ */}
            <div className="lg:col-span-2" data-testid="gc-bid-right">
              <div className="lg:sticky lg:top-24">
                <div className="hb-card p-6" ref={bidFormRef}>
                  {/* Title bar */}
                  <div className="flex items-center gap-3 mb-5 pb-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,206,211,0.1)', border: '1px solid rgba(201,206,211,0.15)' }}><Gavel size={18} weight="duotone" style={{ color: 'var(--gold)' }} /></div>
                    <div><h3 className="text-base font-bold font-heading" style={{ color: 'var(--gold-light)' }}>Bid Workspace</h3><p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Fill out and submit your bid</p></div>
                  </div>

                  {bidSubmitted ? (
                    <div className="text-center py-8" data-testid="bid-success-state">
                      <Check size={44} weight="duotone" className="mx-auto mb-3" style={{ color: '#4ade80' }} />
                      <h3 className="text-lg font-bold font-heading mb-1" style={{ color: 'var(--text-primary)' }}>Bid Submitted!</h3>
                      <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>The owner has been notified.</p>
                      <Button onClick={() => navigate('/dashboard')} className="btn-gold gap-2 text-sm"><ArrowLeft size={14} /> Dashboard</Button>
                    </div>
                  ) : existingBid && existingBid.status === 'changes_requested' && editMode ? (
                    /* ── RESUBMIT EDIT FORM ── */
                    <form onSubmit={handleResubmitBid} className="space-y-4" data-testid="gc-resubmit-form">
                      <div className="rounded-lg px-3 py-2.5 mb-1" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                        <div className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#fbbf24' }}>Owner's Feedback</div>
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{existingBid.change_request_message}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Bid Amount *</Label>
                        <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold" style={{ color: 'var(--gold)' }}>$</span><Input data-testid="resubmit-amount-input" type="number" step="0.01" min="0" value={bidData.amount} onChange={(e) => setBidData({ ...bidData, amount: e.target.value })} placeholder="75,000.00" required className="hb-input pl-7" /></div>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Bid Description / Scope Overview *</Label>
                        <Textarea data-testid="resubmit-proposal-input" value={bidData.proposal} onChange={(e) => setBidData({ ...bidData, proposal: e.target.value })} placeholder="Update your approach..." rows={3} required className="hb-input" />
                      </div>
                      <div><Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Estimated Duration *</Label><Input data-testid="resubmit-duration-input" value={bidData.estimated_duration} onChange={(e) => setBidData({ ...bidData, estimated_duration: e.target.value })} placeholder="e.g. 6-8 weeks" required className="hb-input" /></div>
                      <UploadZone label="Updated Attachments" hint="(optional new files)" testId="resubmit-file-upload" onUpload={handleFileUpload} />
                      {uploadedFiles.length > 0 && (
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>New Uploads ({uploadedFiles.length})</Label>
                          {uploadedFiles.map(f => (
                            <div key={f.id} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                              <div className="flex items-center gap-1.5 min-w-0"><Check size={12} weight="bold" style={{ color: '#4ade80' }} /><span className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{f.name}</span></div>
                              <button type="button" onClick={() => setUploadedFiles(prev => prev.filter(x => x.id !== f.id))} className="p-0.5 text-red-400 hover:text-red-300 shrink-0"><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button type="button" data-testid="cancel-edit-btn" onClick={() => setEditMode(false)} variant="ghost" className="flex-1 btn-ghost text-sm">Cancel</Button>
                        <Button type="submit" data-testid="resubmit-bid-button" disabled={resubmitting || !bidData.amount || !bidData.proposal} className="flex-1 btn-gold gap-2 h-11 text-sm font-semibold">
                          <PaperPlaneTilt size={16} weight="bold" /> {resubmitting ? 'Resubmitting...' : 'Resubmit Bid'}
                        </Button>
                      </div>
                    </form>
                  ) : existingBid && existingBid.status !== 'draft' ? (
                    <div data-testid="bid-existing-state">
                      <div className="flex items-center gap-2 mb-4"><span className={`hb-badge hb-badge-${existingBid.status}`}>{existingBid.status.replace('_', ' ')}</span></div>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="rounded-lg p-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}><div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Amount</div><div className="text-lg font-bold font-heading" style={{ color: 'var(--gold-light)' }}>${existingBid.amount?.toLocaleString()}</div></div>
                        <div className="rounded-lg p-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}><div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Duration</div><div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{existingBid.estimated_duration}</div></div>
                      </div>
                      {existingBid.proposal && <p className="text-sm mb-3 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{existingBid.proposal}</p>}
                      {existingBid.status === 'changes_requested' && existingBid.change_request_message && (
                        <div className="rounded-lg px-3 py-2.5 mb-3" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                          <div className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#fbbf24' }}>Changes Requested by Owner</div>
                          <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{existingBid.change_request_message}</p>
                        </div>
                      )}
                      {existingBid.status === 'changes_requested' && (
                        <Button data-testid="edit-resubmit-btn" onClick={enterEditMode} className="w-full gap-2 text-sm font-semibold h-11 mt-2" style={{ background: 'linear-gradient(180deg, #f59e0b, #d97706)', color: '#0a0a0a', border: 'none' }}>
                          <PaperPlaneTilt size={16} weight="bold" /> Edit & Resubmit Bid
                        </Button>
                      )}
                      {existingBid.status === 'accepted' && <Button data-testid="go-to-hub-button" onClick={() => navigate(`/projects/${projectId}/hub`)} className="btn-gold gap-2 w-full text-sm mt-2">Open Project Hub</Button>}
                    </div>
                  ) : !canBid ? (
                    <div className="text-center py-8"><Buildings size={36} weight="duotone" className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} /><p className="text-sm" style={{ color: 'var(--text-muted)' }}>{project.status === 'awarded' ? 'Project awarded.' : 'Bidding closed.'}</p></div>
                  ) : (
                    <form onSubmit={handleSubmitBid} className="space-y-4" data-testid="gc-bid-form">
                      {/* Bid Amount */}
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Bid Amount *</Label>
                        <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold" style={{ color: 'var(--gold)' }}>$</span><Input data-testid="bid-amount-input" type="number" step="0.01" min="0" value={bidData.amount} onChange={(e) => setBidData({ ...bidData, amount: e.target.value })} placeholder="75,000.00" required className="hb-input pl-7" /></div>
                      </div>
                      {/* Bid Description */}
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Bid Description / Scope Overview *</Label>
                        <Textarea data-testid="bid-proposal-input" value={bidData.proposal} onChange={(e) => setBidData({ ...bidData, proposal: e.target.value })} placeholder="Describe your approach, qualifications, and scope..." rows={3} required className="hb-input" />
                      </div>
                      {/* Contact — 2 col */}
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Contact *</Label><Input data-testid="bid-contact-name" value={bidData.contact_name} onChange={(e) => setBidData({ ...bidData, contact_name: e.target.value })} placeholder="Name" required className="hb-input" /></div>
                        <div><Label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Company *</Label><Input data-testid="bid-company-name" value={bidData.company_name} onChange={(e) => setBidData({ ...bidData, company_name: e.target.value })} placeholder="Company" required className="hb-input" /></div>
                        <div><Label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Email *</Label><Input data-testid="bid-email" type="email" value={bidData.email} onChange={(e) => setBidData({ ...bidData, email: e.target.value })} placeholder="Email" required className="hb-input" /></div>
                        <div><Label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Phone *</Label><Input data-testid="bid-phone" type="tel" value={bidData.phone} onChange={(e) => setBidData({ ...bidData, phone: e.target.value })} placeholder="Phone" required className="hb-input" /></div>
                      </div>
                      {/* Duration */}
                      <div><Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Estimated Duration *</Label><Input data-testid="estimated-duration-input" value={bidData.estimated_duration} onChange={(e) => setBidData({ ...bidData, estimated_duration: e.target.value })} placeholder="e.g. 6-8 weeks" required className="hb-input" /></div>

                      {/* Subcontractors */}
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}><UsersThree size={13} /> Subcontractors</Label>
                        <div className="flex gap-2">
                          <Input data-testid="sub-input" value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubcontractor(); } }} placeholder="Company name" className="hb-input flex-1" />
                          <Button type="button" data-testid="add-sub-btn" onClick={addSubcontractor} size="sm" className="btn-ghost shrink-0" style={{ border: '1px solid var(--border-secondary)' }}>Add</Button>
                        </div>
                        {subcontractors.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {subcontractors.map((s, i) => (
                              <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                                {s} <button type="button" onClick={() => setSubcontractors(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300"><X size={10} /></button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Upload: Bid file */}
                      <UploadZone label="Bid Proposal / Scope" hint="(PDF, DOC)" testId="bid-file-upload-zone" onUpload={handleFileUpload} />
                      {/* Upload: Photos */}
                      <UploadZone label="Photos" hint="(site photos, renderings)" testId="bid-photos-upload" onUpload={handleFileUpload} />
                      {/* Upload: COIs */}
                      <UploadZone label="COIs for Subcontractors" hint="(certificates of insurance)" testId="bid-coi-upload" onUpload={handleFileUpload} />
                      {/* Upload: W9s */}
                      <UploadZone label="W9s for Subcontractors" hint="" testId="bid-w9-upload" onUpload={handleFileUpload} />
                      {/* Upload: Other */}
                      <UploadZone label="Other Attachments" hint="(bonds, licenses)" testId="bid-other-upload" onUpload={handleFileUpload} />

                      {/* Upload progress */}
                      {Object.keys(uploadProgress).length > 0 && (
                        <div className="space-y-1.5">
                          {Object.entries(uploadProgress).map(([fid, p]) => (
                            <div key={fid} className="space-y-0.5"><div className="flex justify-between text-[10px]" style={{ color: 'var(--text-muted)' }}><span>Uploading...</span><span>{p}%</span></div><div className="w-full h-1 hb-progress-bg"><div className="h-1 hb-progress-fill transition-all" style={{ width: `${p}%` }} /></div></div>
                          ))}
                        </div>
                      )}
                      {/* Uploaded files list */}
                      {uploadedFiles.length > 0 && (
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Uploaded ({uploadedFiles.length})</Label>
                          {uploadedFiles.map(f => (
                            <div key={f.id} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                              <div className="flex items-center gap-1.5 min-w-0"><Check size={12} weight="bold" style={{ color: '#4ade80' }} /><span className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{f.name}</span></div>
                              <button type="button" onClick={() => setUploadedFiles(prev => prev.filter(x => x.id !== f.id))} className="p-0.5 text-red-400 hover:text-red-300 shrink-0"><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Draft indicator */}
                      {existingBid && existingBid.status === 'draft' && (
                        <div className="rounded-lg px-3 py-2 mb-1" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
                          <p className="text-xs" style={{ color: '#a5b4fc' }}>Draft saved — continue working and submit when ready.</p>
                        </div>
                      )}

                      {/* Submit + Save Draft */}
                      <div className="flex gap-2">
                        <Button type="button" data-testid="save-draft-button" onClick={handleSaveDraft} disabled={savingDraft || Object.keys(uploadProgress).length > 0}
                          className="flex-1 gap-2 h-11 text-sm font-semibold"
                          style={{ background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.25)' }}>
                          <FloppyDisk size={16} weight="bold" /> {savingDraft ? 'Saving...' : draftSaved ? 'Saved!' : 'Save Draft'}
                        </Button>
                        <Button type="submit" data-testid="submit-bid-form-button" disabled={submittingBid || Object.keys(uploadProgress).length > 0 || !bidData.amount || !bidData.proposal} className="flex-1 btn-gold gap-2 h-11 text-sm font-semibold">
                          <PaperPlaneTilt size={16} weight="bold" /> {submittingBid ? 'Submitting...' : 'Submit Bid'}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  //  OWNER / ADMIN VIEW (unchanged)
  // ═══════════════════════════════════════════
  return (
    <div className="hb-bg"><div style={{ width: '100%' }} className="min-h-screen">
      <header className="hb-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button data-testid="back-button" onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="gap-2 btn-ghost"><ArrowLeft size={20} /> Back</Button>
            <h1 className="text-xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>Project Details</h1>
          </div>
          {['awarded', 'in_progress'].includes(project.status) && (
            <Button data-testid="open-project-hub-button" onClick={() => navigate(`/projects/${projectId}/hub`)} className="btn-gold">Open Project Hub</Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {project.photos.length > 0 && (
              <div className="hb-card overflow-hidden">
                {photoUrl ? (
                  <img src={photoUrl} alt={project.title} className="w-full h-96 object-cover" data-testid="project-hero-image" />
                ) : (
                  <div className="w-full h-96 flex items-center justify-center" style={{ background: 'var(--bg-elevated)' }}><ImageSquare size={48} weight="duotone" style={{ color: 'var(--text-muted)' }} /></div>
                )}
              </div>
            )}
            <div className="hb-card p-6" data-testid="project-info">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-3xl font-bold font-heading mb-2" style={{ color: 'var(--gold-light)' }}>{project.title}</h2>
                  <p style={{ color: 'var(--text-secondary)' }}>Posted by {project.owner_name}</p>
                </div>
                <span className={getStatusColor(project.status)}>{project.status.replace('_', ' ')}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center gap-2"><MapPin size={20} weight="duotone" style={{ color: 'var(--gold)' }} /><div><div className="text-xs" style={{ color: 'var(--text-muted)' }}>Location</div><div className="font-medium" style={{ color: 'var(--text-primary)' }}>{project.location}</div></div></div>
                <div className="flex items-center gap-2"><CurrencyDollar size={20} weight="duotone" style={{ color: 'var(--gold)' }} /><div><div className="text-xs" style={{ color: 'var(--text-muted)' }}>Budget Range</div><div className="font-medium" style={{ color: 'var(--text-primary)' }}>{project.budget_range}</div></div></div>
                <div className="flex items-center gap-2"><CalendarBlank size={20} weight="duotone" style={{ color: 'var(--gold)' }} /><div><div className="text-xs" style={{ color: 'var(--text-muted)' }}>Deadline</div><div className="font-medium" style={{ color: 'var(--text-primary)' }}>{new Date(project.deadline).toLocaleDateString()}</div></div></div>
              </div>
              <div className="space-y-4">
                <div><h3 className="text-lg font-semibold font-heading mb-2" style={{ color: 'var(--gold-light)' }}>Description</h3><p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{project.description}</p></div>
                <div><h3 className="text-lg font-semibold font-heading mb-2" style={{ color: 'var(--gold-light)' }}>Specifications</h3><p className="leading-relaxed whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>{project.specs}</p></div>
                {project.documents && project.documents.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold font-heading mb-3" style={{ color: 'var(--gold-light)' }}>Project Documents</h3>
                    <div className="space-y-2" data-testid="project-documents-list">
                      {project.documents.map(doc => (
                        <button key={doc.id} data-testid={`project-doc-${doc.id}`}
                          onClick={() => openDocument(doc.id, doc.filename)}
                          className="flex items-center justify-between px-4 py-3 rounded-lg transition-colors w-full text-left"
                          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold)'}
                          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
                          <div className="flex items-center gap-3"><FileText size={20} weight="duotone" style={{ color: 'var(--gold)' }} /><div><div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{doc.filename}</div><div className="text-xs" style={{ color: 'var(--text-muted)' }}>{(doc.size / 1024).toFixed(0)} KB</div></div></div>
                          <DownloadSimple size={18} weight="bold" style={{ color: 'var(--gold)' }} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="space-y-6">
            {user.role === 'owner' && (
              <div className="hb-card p-6">
                <h3 className="text-lg font-semibold font-heading mb-4" style={{ color: 'var(--gold-light)' }}>Bids Received ({bids.length})</h3>
                <div className="space-y-3" data-testid="bids-list">
                  {bids.map(bid => (
                    <div key={bid.id} data-testid={`bid-item-${bid.id}`} className="rounded-lg p-4" style={{ border: '1px solid var(--border-primary)', background: 'var(--bg-elevated)' }}>
                      <div className="flex items-start justify-between mb-2"><div><p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{bid.gc_name}</p><p className="text-sm" style={{ color: 'var(--text-muted)' }}>{bid.gc_company}</p></div><span className={getStatusColor(bid.status)}>{bid.status}</span></div>
                      <div className="mb-3"><div className="text-2xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>${bid.amount.toLocaleString()}</div><div className="text-sm" style={{ color: 'var(--text-muted)' }}>{bid.estimated_duration}</div></div>
                      <p className="text-sm mb-3 line-clamp-3" style={{ color: 'var(--text-secondary)' }}>{bid.proposal}</p>
                      {bid.attachments?.length > 0 && (
                        <div className="mb-3 pt-3" style={{ borderTop: '1px solid var(--border-primary)' }}>
                          <p className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Attached ({bid.attachments.length})</p>
                          <div className="space-y-1">{bid.attachments.map((a, i) => (
                            <button key={i} data-testid={`bid-attachment-${bid.id}-${i}`} onClick={() => downloadBidAttachment(bid.id, a.path, a.filename)} className="flex items-center gap-2 text-sm hover:underline" style={{ color: 'var(--gold)' }}><FileText size={16} weight="duotone" /> {a.filename}</button>
                          ))}</div>
                        </div>
                      )}
                      {bid.status === 'pending' && (
                        <Button data-testid={`accept-bid-button-${bid.id}`} onClick={() => handleAcceptBid(bid.id)} size="sm" className="w-full gap-2" style={{ background: '#22c55e', color: '#0a0a0a', fontWeight: 700 }}><Check size={18} weight="bold" /> Accept Bid</Button>
                      )}
                    </div>
                  ))}
                  {bids.length === 0 && (
                    <div className="text-center py-8"><FileText size={48} weight="duotone" className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} /><p style={{ color: 'var(--text-muted)' }}>No bids received yet</p></div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div></div>
  );
};

export default ProjectDetails;
